import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '../context/AuthContext';
import { api, type Workspace, type ApiKeyCreated } from '../api/client';

type Step = 'profile' | 'workspace' | 'apikey' | 'quickstart';

const STEPS: { key: Step; label: string }[] = [
  { key: 'profile', label: 'Profile' },
  { key: 'workspace', label: 'Create Workspace' },
  { key: 'apikey', label: 'API Key' },
  { key: 'quickstart', label: 'Quickstart' },
];

export function Onboarding() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState<Step>('profile');
  const [displayName, setDisplayName] = useState(user?.display_name || '');
  const [workspaceName, setWorkspaceName] = useState('');
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [apiKeyName, setApiKeyName] = useState('default');
  const [createdKey, setCreatedKey] = useState<ApiKeyCreated | null>(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentIndex = STEPS.findIndex((s) => s.key === currentStep);

  function generateSlug(name: string): string {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }

  async function handleNext() {
    setError(null);
    setLoading(true);

    try {
      switch (currentStep) {
        case 'profile': {
          if (displayName.trim()) {
            await api.updateProfile({ display_name: displayName.trim() });
          }
          setCurrentStep('workspace');
          break;
        }
        case 'workspace': {
          if (!workspaceName.trim()) {
            setError('Workspace name is required.');
            break;
          }
          const ws = await api.createWorkspace({
            name: workspaceName.trim(),
            slug: generateSlug(workspaceName),
          });
          setWorkspace(ws);
          setCurrentStep('apikey');
          break;
        }
        case 'apikey': {
          if (!workspace) break;
          if (!apiKeyName.trim()) {
            setError('API key name is required.');
            break;
          }
          const key = await api.createApiKey(workspace.id, {
            name: apiKeyName.trim(),
            roles: ['agent'],
          });
          setCreatedKey(key);
          setCurrentStep('quickstart');
          break;
        }
        case 'quickstart': {
          await api.completeOnboarding();
          await refresh();
          navigate('/dashboard', { replace: true });
          break;
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred.');
    } finally {
      setLoading(false);
    }
  }

  function handleBack() {
    setError(null);
    const idx = currentIndex;
    if (idx > 0) {
      setCurrentStep(STEPS[idx - 1].key);
    }
  }

  async function handleCopyKey() {
    if (!createdKey) return;
    await navigator.clipboard.writeText(createdKey.key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-0">
      <div className="w-full max-w-lg">
        {/* Step indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {STEPS.map((step, idx) => (
              <div key={step.key} className="flex items-center">
                <div
                  className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium ${
                    idx <= currentIndex
                      ? 'bg-gradient-to-r from-accent-teal to-accent-violet text-text-primary'
                      : 'bg-surface-2 text-text-secondary'
                  }`}
                >
                  {idx + 1}
                </div>
                {idx < STEPS.length - 1 && (
                  <div
                    className={`mx-2 h-0.5 w-12 ${
                      idx < currentIndex ? 'bg-gradient-to-r from-accent-teal to-accent-violet' : 'bg-surface-2'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="mt-2 flex justify-between">
            {STEPS.map((step) => (
              <span key={step.key} className="text-xs text-text-secondary">
                {step.label}
              </span>
            ))}
          </div>
        </div>

        {/* Card */}
        <div className="rounded-xl border border-border-dark bg-surface-1 p-8 shadow-sm">
          {error && (
            <div className="mb-6 rounded-lg border border-[rgba(248,113,113,0.2)] bg-[rgba(248,113,113,0.12)] px-4 py-3 text-sm text-[var(--color-error)]">
              {error}
            </div>
          )}

          {/* Step: Profile */}
          {currentStep === 'profile' && (
            <div>
              <h2 className="text-xl font-semibold text-text-primary">
                Welcome to Palaryn
              </h2>
              <p className="mt-2 text-sm text-text-secondary">
                Let's confirm your profile details to get started.
              </p>
              <div className="mt-6">
                <label
                  htmlFor="displayName"
                  className="block text-sm font-medium text-text-primary"
                >
                  Display name
                </label>
                <input
                  id="displayName"
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Your name"
                  className="mt-1 w-full rounded-lg border border-border-dark bg-surface-2 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-accent-teal focus:outline-none focus:ring-1 focus:ring-accent-teal"
                />
              </div>
              {user?.email && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-text-primary">
                    Email
                  </label>
                  <p className="mt-1 text-sm text-text-secondary">{user.email}</p>
                </div>
              )}
            </div>
          )}

          {/* Step: Workspace */}
          {currentStep === 'workspace' && (
            <div>
              <h2 className="text-xl font-semibold text-text-primary">
                Create your workspace
              </h2>
              <p className="mt-2 text-sm text-text-secondary">
                A workspace groups your API keys, budgets, and policies
                together.
              </p>
              <div className="mt-6">
                <label
                  htmlFor="workspaceName"
                  className="block text-sm font-medium text-text-primary"
                >
                  Workspace name
                </label>
                <input
                  id="workspaceName"
                  type="text"
                  value={workspaceName}
                  onChange={(e) => setWorkspaceName(e.target.value)}
                  placeholder="My Organization"
                  className="mt-1 w-full rounded-lg border border-border-dark bg-surface-2 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-accent-teal focus:outline-none focus:ring-1 focus:ring-accent-teal"
                />
                {workspaceName && (
                  <p className="mt-2 text-xs text-text-secondary">
                    Slug: {generateSlug(workspaceName) || '...'}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Step: API Key */}
          {currentStep === 'apikey' && (
            <div>
              <h2 className="text-xl font-semibold text-text-primary">
                Generate your first API key
              </h2>
              <p className="mt-2 text-sm text-text-secondary">
                Create an API key to authenticate your agents with the gateway.
              </p>
              <div className="mt-6">
                <label
                  htmlFor="apiKeyName"
                  className="block text-sm font-medium text-text-primary"
                >
                  Key name
                </label>
                <input
                  id="apiKeyName"
                  type="text"
                  value={apiKeyName}
                  onChange={(e) => setApiKeyName(e.target.value)}
                  placeholder="default"
                  className="mt-1 w-full rounded-lg border border-border-dark bg-surface-2 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-accent-teal focus:outline-none focus:ring-1 focus:ring-accent-teal"
                />
              </div>
            </div>
          )}

          {/* Step: Quickstart */}
          {currentStep === 'quickstart' && (
            <div>
              <h2 className="text-xl font-semibold text-text-primary">
                You're all set
              </h2>
              <p className="mt-2 text-sm text-text-secondary">
                Your workspace and API key are ready. Here's how to make your
                first tool call through the gateway.
              </p>

              {createdKey && (
                <div className="mt-6">
                  <label className="block text-sm font-medium text-text-primary">
                    Your API key
                  </label>
                  <div className="mt-1 flex items-center gap-2">
                    <code className="flex-1 overflow-x-auto rounded-lg bg-surface-2 px-3 py-2 text-xs text-text-primary">
                      {createdKey.key}
                    </code>
                    <button
                      onClick={handleCopyKey}
                      className="rounded-md bg-surface-3 px-3 py-2 text-xs font-medium text-text-primary transition-colors hover:opacity-80"
                    >
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                  <p className="mt-2 text-xs text-[var(--color-warning)]">
                    Save this key now -- it will not be shown again.
                  </p>
                </div>
              )}

              <div className="mt-6">
                <label className="block text-sm font-medium text-text-primary">
                  Example: Execute an HTTP GET
                </label>
                <pre className="mt-1 overflow-x-auto rounded-lg bg-surface-0 p-4 text-xs text-text-primary">
{`curl -X POST http://localhost:3000/v1/tool/execute \\
  -H "Authorization: Bearer ${createdKey?.key || '<YOUR_API_KEY>'}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "tool_call_id": "test-001",
    "task_id": "task-001",
    "tool": { "name": "http.get", "capability": "read" },
    "args": {
      "method": "GET",
      "url": "https://api.example.com/data"
    },
    "actor": { "id": "agent-1", "type": "agent" }
  }'`}
                </pre>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="mt-8 flex items-center justify-between">
            <button
              onClick={handleBack}
              disabled={currentIndex === 0}
              className="rounded-lg border border-border-dark px-4 py-2 text-sm font-medium text-text-primary transition-colors hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Back
            </button>
            <button
              onClick={handleNext}
              disabled={loading}
              className="rounded-lg bg-gradient-to-r from-accent-teal to-accent-violet px-6 py-2 text-sm font-medium text-text-primary transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading
                ? 'Working...'
                : currentStep === 'quickstart'
                  ? 'Go to Dashboard'
                  : 'Continue'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
